import { useMemo } from 'react';
import Fuse, { type IFuseOptions } from 'fuse.js';
import { buildPageRegistry, PageEntry } from '../config/page-registry';
import { useAuth } from '../contexts/AuthContext';
import { useModule } from '../contexts/ModuleContext';
import { useCanOpenPage } from '../modules/useCanOpenPage';

const MIN_QUERY_LENGTH = 2;
const MAX_RESULTS = 5;
const FUSE_OPTIONS: IFuseOptions<PageEntry> = {
  keys: [
    { name: 'label', weight: 1.0 },
    { name: 'keywords', weight: 0.7 },
    { name: 'moduleName', weight: 0.3 },
  ],
  threshold: 0.35,
  includeScore: true,
  shouldSort: true,
};

/**
 * Client-side fuzzy search over the page registry.
 * Returns up to 5 PageEntry matches for the given query.
 * Runs instantly — no debounce needed for <150 items.
 *
 * @param query - Search string from the command palette input
 * @returns Matched pages sorted by relevance, empty if query < 2 chars
 */
export function usePageSearch(query: string): PageEntry[] {
  const { isManagerOrAdmin } = useAuth();
  const isManager = isManagerOrAdmin();
  // userModules is memoized in ModuleContext — reference-stable across renders
  const { userModules } = useModule();
  const canOpenPage = useCanOpenPage();

  const fuse = useMemo(() => {
    const registry = buildPageRegistry({
      isManagerOrAdmin: isManager,
      accessibleModules: userModules,
      canOpenPage,
    });
    return new Fuse(registry, FUSE_OPTIONS);
  }, [isManager, userModules, canOpenPage]);

  return useMemo(() => {
    if (!query || query.length < MIN_QUERY_LENGTH) return [];
    const results = fuse.search(query, { limit: MAX_RESULTS });
    return results.map(r => r.item);
  }, [query, fuse]);
}
